import { ChangeEvent, useCallback, useRef, useState } from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import { CellRendererProps, useCell } from "components/Bable"
import {
  FormulaPart,
  MediaField,
  MediaType,
} from "../../GlobalMediaTypes/types"
import { css, cx } from "@emotion/css"
import { globalState, useContextOptions } from "."

import Button from "ui/buttons/Button"
import { CalculatedFormulaModal } from "../../components/CalculatedFormulaModal"
import { Checkbox } from "@material-ui/core"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import GhostTextInput from "ui/inputs/GhostTextInput"
import MoreActions from "components/MoreActions"
import { MoreButtonBlock } from "components/buttons/MoreButtonBlock"
import { MultiSelect } from "../../components/MultiSelect"
import Options from "components/inputs/Options"
import { OptionsListModal } from "../../components/OptionsListModal"
import WarningIcon from "@material-ui/icons/Error"
import { patchField } from "api/globalMediaTypes"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useMutation } from "react-query"
import { useRow } from "components/Bable/data/useRow"
import { useToggle } from "hooks/useToggle"

const CheckBoxCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const { onUpdate } = useContextOptions()
  const { hasAuthority } = useAuthenticated()
  const { t } = useI18n()

  const [value, setValue, error] = useCell<MediaField>(
    rowId,
    dataKey as keyof MediaField,
    "",
  )
  const row = useRow<MediaField>(rowId)
  let old = useRef(value)

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.checked)
    onUpdate(row.value, dataKey, e.currentTarget.checked).then(
      () => (old.current = value),
    )
  }

  return (
    <>
      <Checkbox
        disabled={!hasAuthority(["AUTH_EDIT_MEDIA_FIELD"])}
        size="medium"
        color="primary"
        classes={{ root: styles.checkbox }}
        checked={value as boolean}
        onChange={onChange}
      />
      {error ? (
        <>
          <div
            className={css`
              position: absolute;
              top: 0;
              color: var(--red);
            `}
            title={t(error)}
          >
            <WarningIcon fontSize="small" color="inherit" />
          </div>
          <span className={cx("BodyBody-xsRedRegular", styles.errorMsg)}>
            {t(error)}
          </span>
        </>
      ) : null}
    </>
  )
}

const EditableCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const { onUpdate } = useContextOptions()
  const { hasAuthority } = useAuthenticated()
  const { t } = useI18n()

  const [value, setValue, error] = useCell<MediaField>(
    rowId,
    dataKey as keyof MediaField,
    "",
  )
  const row = useRow<MediaField>(rowId)
  let old = useRef(value)

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
  }
  const onBlur = () => {
    const hasValueDiff = old.current !== value
    if (dataKey !== undefined && hasValueDiff) {
      onUpdate(row.value, dataKey, value).then(() => (old.current = value))
    }
  }

  return (
    <>
      <GhostTextInput
        value={value as string}
        onChange={onChange}
        onBlur={onBlur}
        fullWidth
        className={cx(styles.input, styles.ghostInput, {
          [styles.error]: !!error,
        })}
        disabled={!hasAuthority(["AUTH_EDIT_MEDIA_FIELD"])}
      />
      {error ? (
        <div
          className={css`
            position: absolute;
            top: 0;
            color: var(--red);
          `}
          title={t(error)}
        >
          <WarningIcon fontSize="small" color="inherit" />
        </div>
      ) : null}
    </>
  )
}

const SelectCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const {
    semanticsOptions,
    typesOptions,
    listOptions,
    formatsOptions,
    formulas,
    onUpdate,
    disabled,
    configScope,
    configOwnerId,
  } = useContextOptions()
  const { t } = useI18n()
  const { open, onOpen, onClose } = useToggle()
  const { hasAuthority } = useAuthenticated()
  const [value, setValue, error] = useCell<MediaField>(
    rowId,
    dataKey as keyof MediaField,
    "",
  )
  const row = useRow<MediaField>(rowId)

  const handleChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    if (value) {
      setValue(value)
    }
  }

  const onBlur = () => {
    if (dataKey !== undefined && !!value) {
      onUpdate(row.value, dataKey, value)
    }
  }

  let cellOptions = [] as any
  switch (dataKey) {
    case "semanticCode":
      cellOptions = semanticsOptions || []
      break
    case "type":
      cellOptions = typesOptions(row.value) || []
      break
    case "format":
      cellOptions = formatsOptions(row.value) || []
      break
    default:
      cellOptions = []
  }

  const calcFormulas = formulas(row.value)
  const rawFormulas = Array.from(calcFormulas as FormulaPart[]).map((f) => ({
    ...f,
  }))
  const rawNestedFormulas = rawFormulas?.map((r) => ({
    ...r,
    leftOperand: Object.assign({}, r?.leftOperand),
    rightOperand: Object.assign({}, r?.rightOperand),
  })) as FormulaPart[]
  const options = listOptions(row.value) || [""]
  const hasOptions = options?.every((o: string) => !!o.length)
  const hasFormulas = !!rawNestedFormulas.length
  const isTypeValid =
    dataKey === "type" &&
    typesOptions(row.value)
      ?.map((t: Record<string, string>) => t.value)
      .includes(value)

  const authDisabled = !hasAuthority(["AUTH_EDIT_MEDIA_FIELD"])
  const conditionDisabled = dataKey === "semanticCode" && disabled(row.value)

  const noOptionButtonPermission = !hasOptions && authDisabled
  const noCalcButtonPermission = !hasFormulas && authDisabled

  const hasOptionsBtn =
    dataKey === "type" &&
    value === "List" &&
    isTypeValid &&
    !noOptionButtonPermission

  const hasCalculatedBtn =
    dataKey === "type" &&
    value === "Calculated" &&
    isTypeValid &&
    !noCalcButtonPermission

  return (
    <>
      <div
        className={cx(styles.buttonContainer, {
          [styles.grid]: hasOptionsBtn || hasCalculatedBtn,
        })}
      >
        {!authDisabled ? (
          <Options
            name={dataKey || ""}
            value={value}
            options={cellOptions}
            onChange={handleChange}
            onBlur={onBlur}
            className={cx(styles.ghostSelect, {
              [styles.error]: !!error,
            })}
            disabled={conditionDisabled}
            size="medium"
            InputProps={{
              classes: { root: styles.input },
            }}
            // error={error}
          />
        ) : (
          <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
            {value}
          </div>
        )}
        {hasOptionsBtn ? (
          <>
            <Button
              onClick={onOpen}
              color="primary"
              className={cx(styles.textButton, {
                [styles.buttonHover]: !hasOptionsBtn,
              })}
              fullWidth={false}
            >
              {hasOptions
                ? t("admin.mediaFields.type.viewList")
                : t("admin.mediaFields.type.addList")}
            </Button>
            <OptionsListModal
              open={open}
              onClose={onClose}
              data={options}
              onUpdate={onUpdate}
              row={row.value}
            />
          </>
        ) : null}
        {hasCalculatedBtn ? (
          <>
            <Button
              onClick={onOpen}
              color="primary"
              className={cx(styles.textButton, {
                [styles.buttonHover]: !hasCalculatedBtn,
              })}
              fullWidth={false}
            >
              {hasFormulas
                ? t("admin.mediaFields.calculated.viewFormula")
                : t("admin.mediaFields.calculated.addFormula")}
            </Button>
            <CalculatedFormulaModal
              open={open}
              onClose={onClose}
              data={rawNestedFormulas}
              row={row.value}
              configScope={configScope}
              configOwnerId={configOwnerId}
            />
          </>
        ) : null}
      </div>
    </>
  )
}

const MultipleSelectCell = (props: CellRendererProps) => {
  const { t } = useI18n()
  const { dataKey, rowId } = props
  const { mediaTypesList } = useContextOptions()
  const [value, setValue] = useCell<MediaField>(rowId, "mediaTypes", "")
  const row = useRow<MediaField>(rowId)
  const { hasAuthority } = useAuthenticated()
  const mediaTypes = !!(value as MediaType[])?.length
    ? (value as MediaType[])
    : []
  const [open, setOpen] = useState<boolean>(false)
  const [errors, setErrors] = useState<ErrorsMap | undefined>({})

  // ** patch field inputs
  const [patch] = useMutation(patchField, {
    onSuccess: (d: MediaField) => globalState.updateRow(d),
    onError: (e: ApiError) => {
      const errorMap = e?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const onUpdate = useCallback(
    async (row, label, value) => {
      patch({ id: row.id, [label]: value })
    },
    [patch],
  )

  const onBlur = () => {
    onUpdate(
      row.value,
      "mediaTypeIds",
      mediaTypes.map((v: MediaType) => v?.id),
    )
    setOpen(false)
  }

  const onOpen = () => {
    setOpen(true)
  }

  const handleToggleOption = (selectedOptions: Array<any>) => {
    const _v = selectedOptions.map((x) => ({ ...x }))
    setValue(_v)
    setErrors(undefined)
  }

  const handleClearOptions = () => setValue([])

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setValue(mediaTypesList)
    } else {
      handleClearOptions()
    }
  }

  const disabled = !hasAuthority(["AUTH_EDIT_MEDIA_FIELD"])

  const showFirstError = !!errors ? errors[Object.keys(errors ?? {})[0]] : ""

  return open ? (
    <MultiSelect
      items={mediaTypesList || []}
      getOptionLabel={(option: Record<string, string>) => option?.name}
      selectedValues={value as MediaType[]}
      renderOption={(option: Record<string, string>) => option?.name}
      selectAllOption={{ name: t("filters.all"), value: "select-all" }}
      selectAllValue="select-all"
      onToggleOption={handleToggleOption}
      onClearOptions={handleClearOptions}
      onSelectAll={handleSelectAll}
      onBlur={onBlur}
      paperClassName={css`
        width: auto;
      `}
      autofocus
      inputClassName={styles.mediatypeWidth}
    />
  ) : (
    <>
      <MoreButtonBlock
        options={mediaTypes}
        error={showFirstError}
        onOpen={onOpen}
        dataKey={dataKey}
        name="admin.mediaFields"
        disabled={disabled}
      />
      {!!errors ? (
        <span className={cx("BodyBody-xsRedRegular", styles.errorMsg)}>
          {t(showFirstError)}
        </span>
      ) : null}
    </>
  )
}

const styles = {
  checkbox: css`
    > span > svg {
      width: var(--spacing-xl);
      height: var(--spacing-xl);
    }
  `,
  cell: css`
    display: flex;
    flex-direction: column;
    line-height: 0.7;
    width: 100%;
  `,
  input: css`
    font-size: 13px;
  `,
  grid: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: var(--spacing-xxs);
  `,
  ghostInput: css`
    :focus-within {
      border: 2px solid var(--primary-main, teal);
    }
  `,
  ghostSelect: css`
    align-items: center;
    border: none;

    outline: none;

    .MuiSvgIcon-root {
      display: none;
    }

    :not([data-disabled="true"]):hover {
      border-color: var(--primary-main, teal);

      .MuiSvgIcon-root {
        display: block;
      }
    }

    :focus,
    :focus-within {
      border-color: var(--primary-main, teal);

      .MuiSvgIcon-root {
        display: block;
      }
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }

    .MuiSelect-outlined.MuiSelect-outlined {
      padding-top: var(--spacing-xs);
      padding-bottom: var(--spacing-xs);
      padding-right: var(--spacing-l);
    }

    .MuiSelect-iconOpen {
      display: block;
    }
  `,
  buttonContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    border: 1px solid transparent;
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 14px;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-active: transparent;
    --btn-bg-focus-ring-color: transparent;
    --btn-bg-hover: transparent;
    padding-left: var(--spacing-m);
    padding-right: var(--spacing-m);

    .label {
      justify-content: flex-start;
    }

    &:focus {
      box-shadow: none;
    }

    &:hover {
      background-color: transparent;
      border: 1px solid transparent;
    }
  `,
  buttonHover: css`
    &:hover {
      border: 1px solid var(--primary-main);
    }
  `,
  error: css`
    border: 1px solid var(--red);
  `,
  errorMsg: css`
    margin: 0 5px;
  `,
  mediatypeWidth: css`
    width: 100%;
  `,
  readonly: css`
    margin: var(--spacing-m);
  `,
}

const commonColumns = [
  {
    id: "name",
    Header: "label",
    dataKey: "name",
    resizable: true,
    sortable: true,
    Cell: EditableCell,
  },
  {
    id: "semanticCode",
    Header: "semantic",
    dataKey: "semanticCode",
    resizable: true,
    sortable: true,
    Cell: SelectCell,
  },
  {
    id: "type",
    Header: "type",
    dataKey: "type",
    resizable: true,
    sortable: true,
    Cell: SelectCell,
  },
  {
    id: "deliverable",
    Header: "deliverable",
    dataKey: "deliverable",
    resizable: true,
    Cell: CheckBoxCell,
  },
  {
    id: "4",
    Header: "media type",
    dataKey: "mediaTypes",
    resizable: true,
    Cell: MultipleSelectCell,
  },
  {
    id: "5",
    Header: "format",
    dataKey: "format",
    resizable: true,
    Cell: SelectCell,
  },
]

const actionColumn = [
  {
    id: "6",
    Header: "actions",
    dataKey: "",
    width: 80,
    Cell: function (props: CellRendererProps) {
      const { t } = useI18n()
      const { onDelete } = useContextOptions()
      const { rowId } = props
      const row = useRow<MediaField>(rowId)
      return (
        <MoreActions
          options={[
            // DELETE
            {
              label: t("client-groups.list.delete.action"),
              icon: <DeleteIcon />,
              onClick: () => {
                onDelete(row.value)
              },
            },
          ]}
        />
      )
    },
  },
]

export const GLOBAL_MEDIA_FIELDS_COLUMNS = (actionAllowed: boolean) =>
  actionAllowed ? [...commonColumns, ...actionColumn] : commonColumns
